(ns minorodata.components.pipeline-editor.details.transform
  (:require
   ["allotment" :refer [Allotment]]
   ["lucide-react" :refer [X]]
   ["react" :as react]
   [applied-science.js-interop :as jsi]
   [goog.functions :refer [debounce]]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.app.utils :as app.utils :refer [format-date]]
   [ui.data-grid.preview :as data-grid-preview]
   [minorodata.components.log.table :refer [logs-row]]
   [minorodata.components.monaco.completions :as monaco.completions]
   [minorodata.components.monaco.editor :as monaco.editor]
   [minorodata.components.pipeline-editor.details.utils :refer [preview-command
                                                                row-count-dropdown
                                                                selected-node-bar
                                                                tab-actions]]
   [minorodata.components.spinner.core :as spinner]
   [minorodata.components.transformations.modals :refer [transform-notifications
                                                         transform-settings
                                                         transform-webhooks]]
   [minorodata.components.transformations.transforms :refer [transform-versions]]
   [minorodata.logs.model :refer [->logs-view]]
   [minorodata.sandbox.events :refer [reset-preview]]
   [minorodata.sandbox.model :refer [->pipeline-transform-unsaved]]
   [mold-forms.core :as form-component :refer [model->fields]]
   [mold-forms.model :refer [->form-model]]
   [re-posh.core :as re-posh]
   [ui.alert :as alert]
   [ui.button :as button]
   [ui.fields :as fields]
   [ui.table :refer [table tbody th thead tr]]
   [ui.tabs :as tabs]
   [ui.typography :as typography]))

(defn selected-transform-bar [{:keys [selected data loading]}]
  (let [sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])]
    [selected-node-bar {:selected selected
                        :loading  loading
                        :name     (or (:name data) "Transform doesn't exist")
                        :description (when (and (not sandbox?) data)
                                       @(re-posh/subscribe [:transformations.subs/last-status (:transform-id data)]))
                        :sandbox? sandbox?}]))

(defn update-unsaved-transform' [id v]
  (dispatch [:pipeline-editor.events/update-local-transform id v]))

(def update-unsaved-transform
  (debounce update-unsaved-transform' 300))

(defn versions [{:keys [transform]}]
  [transform-versions {:transform transform
                       #_#_:on-rollback-ev on-rollback-ev}])

(defn update-confirm-response? [v]
  (and (map? v)
       (get v :update-count)))

(defn preview-content [{[_ [headers & rows]] :data}]
  (if (update-confirm-response? headers)
    [:div {:class "p-10 py-6"} "Update count: " (:update-count headers) "."]
    (let [dark-mode? @(re-posh/subscribe [:app/prop :app/dark-mode?])]
      [:div {:class "h-full min-h-0 overflow-hidden"}
       [:f> data-grid-preview/table
        {:headers                   headers
         :rows                      rows
         :dark-mode?                dark-mode?
         :height                    "100%"
         :column-width              150
         :sortable?                 true
         :filterable?               true
         :resizable?                true
         :row-numbers?              true
         :suppress-no-rows-overlay? true
         :header-tooltips?          true}]])))

(defn preview-table [{:keys [preview on-change sandbox?]}]
  (let [{:keys [id results]} preview]
    [tabs/tabs-root {:default-value "0"
                     :class "grid h-full min-h-0 w-full grid-rows-[auto_1fr] overflow-hidden"}
     [tabs/tabs-list {:variant :underlined
                      :class "relative w-full border-b px-10 bg-kleene-background-secondary"}
      (for [idx (range (count results))]
        [tabs/tabs-trigger {:key idx :value (str idx) :variant :underlined} (str "Result " (inc idx))])
      [:div {:class "flex-1 flex gap-1 justify-end items-center"}
       [row-count-dropdown {:on-change on-change}]
       [button/button {:variant :secondary
                       :size :icon
                       :icon X
                       :on-click #(if sandbox?
                                    (dispatch [:sandbox-preview/delete [:sandbox-preview/id id]])
                                    (dispatch [:pipeline-transform-preview/delete [:pipeline-transform-preview/id id]]))}]]]

     (for [data (map-indexed vector results)
           :let [[idx] data]]
       [tabs/tabs-content {:key (str id idx)
                           :value (str idx)
                           :class "h-full min-h-0 w-full overflow-hidden"}
        [:f> preview-content {:data data}]])]))


(defn editor' [{:keys [editor-ref transform unsaved-transform preview sandbox? warehouse-type]}]
  (let [{:keys [master-id sql command]} transform
        command (or (:pipeline-transform-unsaved/command unsaved-transform)
                    sql command "")
        on-change (react/useCallback
                   (fn [v]
                     (when sandbox?
                       (when-not unsaved-transform
                         (dispatch [:pipeline-transform-unsaved/add (->pipeline-transform-unsaved {:id master-id})]))
                       (update-unsaved-transform master-id v)))
                   #js [master-id sandbox? (boolean unsaved-transform)])]

    [:> Allotment {:vertical true}
     (when command
       ^{:key (str master-id sandbox?)}
       [:f> monaco.editor/editor
        {:theme     "vs-dark"
         :data-cy   "pipeline-editor"
         :controlled? true
         :options   {:fontSize        12
                     :automaticLayout true
                     :contextmenu     true
                     :readOnly        (not sandbox?)}
         :language            (monaco.editor/warehouse-language warehouse-type)
         :editor-will-mount   (fn [monaco]
                                (monaco.completions/init-completions-provider monaco))
         :editor-did-mount (fn [editor monaco]
                             (jsi/assoc! editor-ref :current editor)
                             (jsi/call editor :addAction
                                       (clj->js {:id          "execute-query"
                                                 :label       "Run Code"
                                                 :contextMenuOrder 0
                                                 :contextMenuGroupId "2_execution"
                                                 :keybindings [(bit-or (jsi/get-in monaco [:KeyMod :CtrlCmd])
                                                                       (jsi/get-in monaco [:KeyCode :Enter]))]
                                                 :run         (partial preview-command {:id master-id})}))
                             (jsi/call editor :addAction
                                       (clj->js {:id          "save-transform"
                                                 :label       "Save"
                                                 :contextMenuOrder 1
                                                 :contextMenuGroupId "2_execution"
                                                 :keybindings [(bit-or (jsi/get-in monaco [:KeyMod :CtrlCmd])
                                                                       (jsi/get-in monaco [:KeyCode :KeyS]))]
                                                 :run         #(dispatch [:sandbox.api/update-transform {:id master-id}])})))
         :on-change on-change
         :value     command}])
     (when preview
       [:f> preview-table {:preview preview
                           :on-change #(preview-command {:id master-id} (jsi/get editor-ref :current))
                           :sandbox? sandbox?}])]))

(defn editor [{:keys [editor-ref transform]}]
  (let [{:keys [master-id]} transform
        sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])
        preview (some->
                 (if sandbox?
                   @(re-posh/subscribe [:sandbox-preview/pull [:sandbox-preview/id master-id]])
                   @(re-posh/subscribe [:pipeline-transform-preview/pull [:pipeline-transform-preview/id master-id]]))
                 app.utils/drop-ns-in-keys)
        unsaved-transform (when sandbox?
                            @(re-posh/subscribe [:pipeline-transform-unsaved/pull [:pipeline-transform-unsaved/id master-id]]))
        warehouse-type    @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])]
    (when transform
      [:f> editor' {:editor-ref        editor-ref
                    :transform         transform
                    :warehouse-type    warehouse-type
                    :unsaved-transform unsaved-transform
                    :preview           preview
                    :sandbox?          sandbox?}])))

(defn pipeline-transform-settings [{:keys [transform]}]
  (let [{:keys [group-id id transform-id]} transform
        transform-id (or transform-id id)
        groups   @(re-posh/subscribe [:pipeline-editor.subs/transform-groups])
        loading? @(re-posh/subscribe [:mold-http/loading? :sandbox.api/fetch-sandbox-transforms])
        sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])]
    [:div {:key transform-id
           :class "mt-4"}
     [form-component/form
      (->form-model
       {:id            :edit-pipeline-transform-form
        :track-changes true
        :changed       (boolean (:changed? transform))
        :validate      (partial form-component/validate-form ::transform-form)
        :on-submit     (fn [form-data]
                         (let [fields (model->fields form-data)]
                           (dispatch [:sandbox.api/update-transform {:id (:master-id transform)
                                                                     :updates fields}])))
        :clean-up      #(dispatch [:form-model/delete [:form-model/id :edit-pipeline-transform-form]])
        :fields        [{:name :name :value (:name transform)}
                        {:name :description :value (:description transform)}
                        {:name :group-id :value group-id}]})
      (fn [fields errors on-submit]
        [:div {:class "pb-kai max-w-2xl px-10"}
         [:div {:class "flex flex-col gap-2"}
          [fields/input
           {:field         (get fields :name)
            :errors        errors
            :controlled?   false
            :id            :transform-name
            :data-cy       :transform-name-input
            :label         "Transform name"}]

          (when-not sandbox?
            [fields/input
             {:field         (get fields :description)
              :errors        errors
              :id            :transform-description
              :textarea      true
              :label         "Transform description"}])

          (when-not sandbox?
            [fields/select
             {:field         (get fields :group-id)
              :errors        errors
              :id            :transform-group
              :data-cy       :transform-group-select
              :label         "Transform group"
              :options       (map (fn [{:pipeline-group/keys [id name]}]
                                    {:value id :text name})
                                  groups)}])]

         [:footer {:class "mt-4 flex justify-end"}
          [button/button {:data-cy  "update-transform-btn"
                          :disabled loading?
                          :on-click on-submit}
           "Save"]]])]]))

(def log-params
  {:items-per-page 50
   :page 0})

(defn recent-logs' [{:keys [transform timezone logs loading? sandbox?]}]
  (react/useEffect
   (fn []
     (dispatch [:logs-view/add (->logs-view (assoc log-params :term (:transform-id transform)))
                {:replace true}])
     (dispatch [:logs.api/fetch])

     (fn []
       (dispatch-sync [:logs-view/delete])))
   #js [transform])

  (cond
    loading?
    [:div {:class "flex gap-2 items-center px-10 py-6"}
     [spinner/spinner-lucide] "Loading logs..."]

    (empty? logs)
    [typography/p {:class "text-kleene-text-secondary px-10 py-6"} "No recent logs"]

    :else
    [:<>
     (when sandbox?
       [alert/alert {:variant :warning}
        [alert/alert-title "Live transform logs are being displayed."]])
     [table {:data-cy       "logs-table"
             :sticky-header true}
      [thead
       [tr {:sticky true}
        [th {:style {:width "180px" :height "52px"}
             :class "border-r"}
         [:span
          "Date"
          [:div {:class "-mt-1"}
           [:span {:class "text-xs text-kleene-dark-05 dark:text-kleene-dark-03"} timezone]]]]
        [th {:style {:width "80px"}
             :class "border-r"}
         "Type"]
        [th {:class "border-r"}
         "Message"]
        [th {:style {:width "80px"}} "Task"]]]
      [tbody {}
       (for [message-id logs]
         ^{:key message-id}
         [logs-row {:message-id   message-id
                    :skip-columns ["source-name" "name"]
                    :interactive  false}])]]]))


(defn recent-logs [{:keys [transform]}]
  (let [logs     @(re-posh/subscribe [:logs-pager/prop :logs-pager/messages])
        loading? @(re-posh/subscribe [:mold-http/loading? :logs.api/fetch])
        timezone @(re-posh/subscribe [:app/prop :app/timezone])
        sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])]
    [:f> recent-logs' {:transform transform
                       :logs      logs
                       :loading?  loading?
                       :timezone  timezone
                       :sandbox?  sandbox?}]))

(defn transform-details-tabs' [{:keys [transform disabled sandbox? tab-request]}]
  (let [{:keys [master-id group-id created-at updated-at created-by updated-by]} transform
        editor-ref (react/useRef nil)
        [tab set-tab] (react/useState "sql")]

    (react/useEffect
     (fn [] (set-tab "sql"))
     #js [master-id])

    ;; A tab asked for from outside — the canvas's `Preview → Logs` (see
    ;; `pipeline-editor.dag/preview!`). Declared after the reset above so it wins when
    ;; both fire in the same commit (a new node previewed straight onto its Logs tab),
    ;; and consumed so the next request re-opens the tab even if the user has switched
    ;; away from it since. Only the addressed pane may consume it, or a request whose
    ;; own pane never mounted lands on this one instead.
    (react/useEffect
     (fn []
       (when (and tab-request (= (:id tab-request) master-id))
         (set-tab (:tab tab-request))
         (dispatch [:pipeline-editor.events/request-details-tab nil]))
       js/undefined)
     #js [tab-request master-id])

    [tabs/tabs-root {:value           tab
                     :variant         :tab
                     :class           "grid grid-rows-[auto_1fr] overflow-hidden"
                     :on-value-change set-tab}
     [tabs/tabs-list {:variant :tab :class "px-10"}
      [tabs/tabs-trigger {:value "sql" :variant :tab :disabled disabled} "SQL"]
      [tabs/tabs-trigger {:value "logs" :variant :tab :disabled disabled} "Logs"]
      [tabs/tabs-trigger {:value "settings" :variant :tab :disabled disabled} "Settings"]
      (when-not sandbox?
        [:<>
         [tabs/tabs-trigger {:value "history" :variant :tab :disabled disabled} "History"]
         [tabs/tabs-trigger {:value "webhooks" :variant :tab :disabled disabled} "Webhooks"]
         [tabs/tabs-trigger {:value "notifications" :variant :tab :disabled disabled} "Notifications"]])

      [tab-actions {:tab tab
                    :id   master-id
                    :transform transform
                    :editor-ref editor-ref
                    :disabled disabled}]]

     [tabs/tabs-content {:value "sql"}
      [editor {:transform  transform
               :editor-ref editor-ref}]]

     [tabs/tabs-content {:value "logs" :class "overflow-y-auto"}
      [recent-logs {:transform transform}]]
     [tabs/tabs-content {:value "settings" :class "overflow-y-auto"}
      (if sandbox?
        [pipeline-transform-settings {:transform transform}]
        [:div {:class "flex flex-col gap-4 px-10 py-6"}
         [:div {:class "flex flex-col gap-1"}
          [typography/p "Created " (format-date created-at) " by " (:searchable-user/username created-by)]
          [typography/p "Updated " (format-date updated-at) " by " (:searchable-user/username updated-by)]]
         [transform-settings {:transform transform}]])]
     [tabs/tabs-content {:value "history" :class "overflow-hidden"}
      [versions {:transform transform}]]
     (when-not sandbox?
       [:<>
        [tabs/tabs-content {:value "webhooks" :class "overflow-y-auto"}
         [:div {:class "flex flex-col gap-4 px-10 py-6 max-w-6xl"}
          [transform-webhooks {:master-id master-id
                               :group-id group-id}]]]
        [tabs/tabs-content {:value "notifications" :class "overflow-y-auto"}
         [:div {:class "max-w-2xl px-10 py-6"}
          [transform-notifications {:master-id master-id}]]]])]))

(defn transform-details-tabs [props]
  (let [sandbox?    @(re-posh/subscribe [:sandbox.subs/enabled?])
        tab-request @(re-posh/subscribe [:pipeline-editor.subs/details-tab-request])]
    [:f> transform-details-tabs' (assoc props
                                        :sandbox?    sandbox?
                                        :tab-request tab-request)]))



(defn transform-details' [{:keys [selected transform loading sandbox?]}]
  (react/useEffect
   (fn []
     (when (not sandbox?)
       (dispatch [:transform.api/get-latest-version-by-master-id
                  {:master-id (:id selected)}]))
     js/undefined)
   #js [(:id selected) sandbox?])

  [:div {:class "grid h-full grid-rows-[auto_1fr] overflow-hidden"}
   [selected-transform-bar {:selected selected
                            :data transform
                            :loading loading}]
   (cond
     (not-empty transform)
     [transform-details-tabs {:transform transform
                              :master-id (:id selected)
                              :group-id (:group-id selected)
                              :disabled loading}]

     loading
     [:div {:class "flex items-center justify-center h-full"}
      [spinner/with-text {:text "Loading transform..."}]]

     :else
     [:div {:class "flex flex-col gap-2 items-center justify-center h-full"}
      [typography/h2 "Transform doesn't exist in " (if sandbox? "sandbox" "live") " environment"]
      (when (:preview selected)
        [button/button {:variant :secondary
                        :on-click #(dispatch [:app.events/navigate-query reset-preview])}
         "Close preview"])])])

(defn transform-details [{:keys [selected]}]
  (let [transform @(re-posh/subscribe [:pipeline-editor.subs/full-transform (:id selected)])
        loading   (or @(re-posh/subscribe [:mold-http/loading? :sandbox.api/fetch-sandbox-transforms])
                      @(re-posh/subscribe [:mold-http/loading? :pipeline-editor.api/fetch-single-kioku-transform])
                      @(re-posh/subscribe [:mold-http/loading? :dag.api/load-dag-metadata]))
        sandbox?  @(re-posh/subscribe [:sandbox.subs/enabled?])]
    [:f> transform-details' {:selected selected
                             :transform transform
                             :loading loading
                             :sandbox? sandbox?}]))
